// Modified GamesGrid.js
import React, { useEffect, useState } from "react";
import search from "../../../images/search.svg";
import Pagination from "../../../Components/Pagination";
import { Gamepad2, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GameModes } from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setLoginModal } from "../../../store/auth-slice";
import { toastService } from "../../notificationPage/ToastContainer";
import { allowsDemo, formatGameName } from "../../../utils";
import PragmaticGameCard from "./pragmaticLiveGameCard";

// Skeleton component for game cards
const GameCardSkeleton = () => {
  return (
    <div className="relative aspect-[3/2] rounded-lg overflow-hidden shadow-lg animate-pulse">
      {/* Skeleton image placeholder */}
      <div className="w-full h-full bg-gray-300 dark:bg-darktitleColor"></div>

      {/* Skeleton name bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gray-400 dark:bg-bluedarkmode py-2 px-3">
        <div className="h-4 w-3/4 mx-auto bg-gray-300 dark:bg-darktitleColor rounded"></div>
      </div>
    </div>
  );
};

const GamesGrid = ({
  games,
  onGameClick,
  isVirtual = false,
  isLoading = false,
  title,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const user = useSelector(selectUser);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredGames, setFilteredGames] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeCardId, setActiveCardId] = useState(null);

  const handleToggleActive = (cardId) => {
    // If the same card is clicked, close it. Otherwise, open the new one
    setActiveCardId(activeCardId === cardId ? null : cardId);
  };

  const itemsPerPage = 18;

  useEffect(() => {
    if (games && Array.isArray(games)) {
      const filtered = games.filter((game) => {
        if (!searchTerm.trim()) return true; // Show all games if no search term

        const searchLower = searchTerm.toLowerCase().trim();

        // Search in multiple fields
        const searchableFields = [
          game.alias, // Game alias
          game.name, // Game name (from API or formatted)
          game.gameName, // Legacy gameName field
          game.title, // Title from API
          game.provider, // Provider name
          game.subProvider, // Sub-provider (for Kelt games)
        ];

        return searchableFields.some(
          (field) =>
            field &&
            typeof field === "string" &&
            field.toLowerCase().includes(searchLower)
        );
      });

      setFilteredGames(filtered);
      setCurrentPage(1); // Reset to first page when search changes
    } else {
      // Fallback for legacy object format
      if (games && typeof games === "object" && !Array.isArray(games)) {
        const filtered = Object.entries(games).filter(([alias, game]) => {
          if (!searchTerm.trim()) return true;

          const searchLower = searchTerm.toLowerCase().trim();
          const gameNameToSearch = game.gameName || game.name || alias;

          return (
            gameNameToSearch.toLowerCase().includes(searchLower) ||
            alias.toLowerCase().includes(searchLower)
          );
        });
        setFilteredGames(filtered.map(([alias, game]) => ({ ...game, alias })));
        setCurrentPage(1);
      } else {
        setFilteredGames([]);
      }
    }
  }, [searchTerm, games]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredGames.length / itemsPerPage);

  // Get current games for pagination
  const getCurrentGames = () => {
    // Uncomment these lines if you want to enable pagination
    // const startIndex = (currentPage - 1) * itemsPerPage;
    // const endIndex = startIndex + itemsPerPage;
    // return filteredGames.slice(startIndex, endIndex);
    return filteredGames;
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of grid when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePlayGame = (provider, alias, mode) => {
    if (mode === GameModes.REAL && !user) {
      dispatch(setLoginModal(true)); // Open login modal
      toastService.error("Error! Please login to proceed!!");
      return; // Stop further execution
    }

    // Navigate to the game page with query parameters
    if (isVirtual) {
      navigate(`/game/${alias}?provider=${provider}&mode=${mode}`);
    } else {
      navigate(`/game/${alias}?provider=${provider}&mode=${mode}`);
    }
  };

  // Generate skeleton placeholders when loading
  const renderSkeletons = () => {
    return Array(12)
      .fill(0)
      .map((_, index) => <GameCardSkeleton key={`skeleton-${index}`} />);
  };

  // Helper function to get display name for a game
  const getGameDisplayName = (game) => {
    return (
      game.name || game.gameName || game.title || game.alias || "Unknown Game"
    );
  };

  // Helper function to get game provider
  const getGameProvider = (game) => {
    return game.provider || game.subProvider || "unknown";
  };

  return (
    <div className="p-2 md:p-3 s15:p-2 bg-white dark:bg-bluedarkmode md:rounded-md shadow-inputshadow">
      {/* Search Bar */}
      <div className="relative rounded-md shadow-inputshadow inputshadow mb-3 s15:mb-5">
        <input
          type="text"
          className="p-[8px_40px_8px_8px] s18:p-[12px_40px_12px_12px] rounded-md w-full text-xs lg:text-sm s18:text-base font-normal placeholder:text-darkblue/20 dark:placeholder:text-white/30 bg-white dark:bg-darktitleColor text-darkblue dark:text-white"
          placeholder="Search Games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
        />
        <img
          src={search}
          alt="search"
          className="absolute top-1/2 -translate-y-1/2 right-4 dark:invert"
        />
      </div>

      {/* Games Grid */}
      <div>
        <div className="flex items-center justify-between mb-3 s15:mb-5">
          <p className="text-darkblue dark:text-white text-sm lg:text-base s15:text-lg font-bold capitalize">
            {title}
          </p>
          {/* Search results count */}
          {searchTerm && (
            <p className="text-darkblue/60 dark:text-white/60 text-xs lg:text-sm">
              {filteredGames.length} game{filteredGames.length !== 1 ? "s" : ""}{" "}
              found
            </p>
          )}
        </div>

        {/* No results message */}
        {!isLoading && filteredGames.length === 0 && searchTerm && (
          <div className="text-center py-8">
            <p className="text-darkblue/60 dark:text-white/60 text-sm lg:text-base">
              No games found for "{searchTerm}". Try a different search term.
            </p>
          </div>
        )}

        {/* Grid with skeleton or game cards */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3 s18:gap-[17px] px-0">
          {isLoading
            ? renderSkeletons()
            : getCurrentGames().map((game, index) => {
                // Handle both array format and legacy object format
                const gameAlias = game.alias || `game-${index}`;
                const cardIdentifier = gameAlias;
                const displayName = getGameDisplayName(game);
                const provider = getGameProvider(game);

                return (
                  <GameCard
                    key={`${provider}-${gameAlias}-${index}`} // Ensure unique key
                    alias={gameAlias}
                    imagePath={game.imagePath}
                    gameName={displayName}
                    isVirtual={isVirtual}
                    game={game}
                    provider={provider}
                    onPlay={(mode) => handlePlayGame(provider, gameAlias, mode)}
                    // New props for active state management
                    cardId={cardIdentifier}
                    isActive={activeCardId === cardIdentifier}
                    onToggleActive={handleToggleActive}
                  />
                );
              })}
        </div>
      </div>

      {/* Pagination Component - only show when not loading */}
      {/* {!isLoading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )} */}
    </div>
  );
};
const GameCard = ({
  game,
  alias,
  imagePath,
  gameName,
  isVirtual,
  provider,
  onPlay,
  // New props for managing active state
  isActive,
  onToggleActive,
  cardId, // unique identifier for this card (using alias or fallback)
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device for proper interaction handling
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set on mount
    checkIfMobile();

    // Update on resize
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const handleCardClick = (e) => {
    // Only toggle actions on mobile
    if (isMobile && !e.target.closest("button")) {
      onToggleActive(cardId);
    }
  };

  // Special case for pragmatic provider
  if (provider === "pragmaticlive") {
    return <PragmaticGameCard gameData={game} onPlay={onPlay} />;
  }

  return (
    <div className="group cursor-pointer w-full max-w-full">
      {/* Image + overlays */}
      <div
        className={`relative rounded-t-lg overflow-hidden shadow-lg transition-transform duration-300 hover:scale-105 
        ${isMobile ? "aspect-[3/3]" : "aspect-[3/2]"}`}
        onClick={handleCardClick}
        onMouseLeave={() => isMobile && onToggleActive(null)}
      >
        <img
          src={imagePath || "/api/placeholder/300/200"}
          alt={game.name || alias}
          className="object-cover w-full h-full rounded-t-lg"
        />

        {/* Action overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent
          flex items-center justify-center transition-all duration-300
          ${isActive ? "opacity-100" : "opacity-0 md:group-hover:opacity-100"}`}
        >
          <div className="flex flex-col gap-2 items-center justify-center px-3 w-full">
            {allowsDemo(provider) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPlay(GameModes.DEMO);
                  isMobile && onToggleActive(null);
                }}
                className={`bg-white text-gray-800 font-medium 
                rounded-md hover:bg-gray-100 active:bg-gray-200 
                transition-all shadow-md uppercase tracking-wide 
                focus:outline-none focus:ring-2 focus:ring-gray-300
                flex items-center justify-center gap-1 w-full min-h-[36px]
                ${isMobile ? "py-2 px-2 text-xs" : "py-1.5 px-3 text-xs"}`}
              >
                <Gamepad2 size={isMobile ? 14 : 12} />
                <span className="truncate">Demo</span>
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlay(GameModes.REAL);
                isMobile && onToggleActive(null);
              }}
              className={`dark:bg-darkblue text-white font-medium 
              rounded-md hover:from-blue-600 hover:to-purple-700 
              active:from-blue-700 active:to-purple-800 
              transition-all shadow-md uppercase tracking-wide 
              focus:outline-none focus:ring-2 focus:ring-purple-400 
              flex items-center justify-center gap-1 w-full min-h-[36px]
              ${isMobile ? "py-2 px-2 text-xs" : "py-1.5 px-3 text-xs"}`}
            >
              <Play size={isMobile ? 14 : 12} />
              <span className="truncate">Play</span>
            </button>
          </div>
        </div>

        {/* Mobile indicator */}
        {isMobile && !isActive && (
          <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
            <Play size={16} className="text-white" />
          </div>
        )}
      </div>

      {/* Game name - now BELOW the image */}
      <div
        className={`mt-0 bg-bluedarkmode dark:bg-darktitleColor text-white 
        ${isMobile ? "py-2 px-3" : "py-1 md:py-2 px-2 md:px-3"} rounded-b-lg`}
      >
        <h3
          className={`text-white font-medium text-center truncate
          ${isMobile ? "text-sm" : "text-xs md:text-sm"}`}
        >
          {formatGameName(game.name || alias)}
        </h3>
      </div>
    </div>
  );
};

export default GamesGrid;
